// Bouwt de payload die het publieke intakeformulier naar de Cloud Function
// stuurt. Bewust smal: alleen wat de invuller echt heeft ingevuld gaat mee.
// Vraagtekst en categorie leest de server zelf uit de velddefinitie, zodat een
// aangepaste pagina niet kan bepalen waar een antwoord landt.
// AI-CONTEXT: See docs/patterns.md#cf-public-anonymous-callable
// AI-CONTEXT: See docs/pitfalls.md#dev-write-path-migration-drops-derived-values

/**
 * @typedef {object} IntakeAnswer
 * @property {string} FieldID - Veld waar dit antwoord bij hoort
 * @property {string} Value - Het antwoord zelf
 * @property {string} [RowID] - Matrixrij, alleen bij matrixcellen
 * @property {string} [ColumnID] - Matrixkolom, alleen bij matrixcellen
 * @property {string} [RowTitle] - Rijlabel op het moment van antwoorden
 * @property {string} [ColumnTitle] - Kolomlabel op het moment van antwoorden
 */

const MATRIX_KEYS = ['RowID', 'ColumnID', 'RowTitle', 'ColumnTitle'];

/**
 * @param {object} response
 * @returns {IntakeAnswer}
 */
const toAnswer = (response) => {
    const answer = {
        FieldID: response.FieldID,
        Value: response.Value === undefined || response.Value === null ? '' : response.Value,
    };

    for (const key of MATRIX_KEYS) {
        if (response[key] !== undefined && response[key] !== null) answer[key] = response[key];
    }

    return answer;
};

/**
 * @typedef {object} BuildIntakeSubmissionInput
 * @property {string} client - CompagnyID van de tenant
 * @property {string} formId - ID van het formulier
 * @property {Array<object>} responses - Antwoorden uit de formulierstate
 * @property {Array<{FieldID: string, Value: string}>} [uploadResponses] - Al geuploade bestanden
 * @property {boolean} privacyAccepted - Of het privacyvinkje aan stond
 * @property {object | null} location - Gevonden PDOK-adres, of null
 */

/**
 * @param {BuildIntakeSubmissionInput} input
 */
/**
 * De naam van het lokveld. Moet gelijk zijn aan `HONEYPOT_KEY` in
 * `functions/intakeForms/sanitizeIntakeSubmission.js` — daar wordt hij gelezen.
 *
 * Bewust een naam die wachtwoordbeheerders en browser-invulhulp NIET herkennen:
 * 'email', 'naam' of 'telefoon' zouden automatisch gevuld worden en dan sluiten
 * we echte bezoekers buiten.
 */
const HONEYPOT_KEY = 'website';

const buildIntakeSubmission = ({
    client,
    formId,
    responses = [],
    uploadResponses = [],
    privacyAccepted = false,
    location = null,
    honeypot = '',
}) => ({
    client,
    formId,
    responses: [...responses, ...uploadResponses].map(toAnswer),
    privacyAccepted: privacyAccepted === true,
    location: location || null,
    // Het onzichtbare lokveld gaat altijd mee, ook leeg: zou het alleen bij een
    // waarde meegestuurd worden, dan is de aanwezigheid van de sleutel zelf het
    // verklikkertje.
    [HONEYPOT_KEY]: honeypot || '',
});

export default buildIntakeSubmission;
export { HONEYPOT_KEY };
